import React, { useEffect, useState } from 'react';

const WeatherWidget = ({ location, isDistrictView = false, isStateView = false }) => {
  const [weather, setWeather] = useState(null);
  const [aqiData, setAqiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState(location || 'New Delhi');
  const [error, setError] = useState(null);
  const [userContext, setUserContext] = useState({ type: 'general', state: '', district: '' });

  // Get user context from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        if (user.selectedState && user.selectedDistrict) {
          setUserContext({ type: 'district', state: user.selectedState, district: user.selectedDistrict });
        } else if (user.selectedState) {
          setUserContext({ type: 'state', state: user.selectedState, district: '' });
        } else {
          setUserContext({ type: 'general', state: '', district: '' });
        }
      } catch {
        setUserContext({ type: 'general', state: '', district: '' });
      }
    }
  }, []);

  // Determine the effective location and view type
  const getEffectiveLocation = () => {
    console.log('Getting effective location. UserContext:', userContext, 'Location prop:', location);

    if (location) {
      console.log('Using provided location prop:', location);
      return location; // Use provided location prop
    }

    if (userContext.type === 'district' && userContext.district) {
      console.log('Using district location:', userContext.district);
      return userContext.district; // Use district for district admin
    }

    if (userContext.type === 'state' && userContext.state) {
      console.log('Using state location:', userContext.state);
      // For state admin, use the state capital or major city for better weather data
      const stateCapitals = {
        'Andhra Pradesh': 'Amaravati',
        'Arunachal Pradesh': 'Itanagar',
        'Assam': 'Guwahati',
        'Bihar': 'Patna',
        'Chhattisgarh': 'Raipur',
        'Goa': 'Panaji',
        'Gujarat': 'Gandhinagar',
        'Haryana': 'Chandigarh',
        'Himachal Pradesh': 'Shimla',
        'Jharkhand': 'Ranchi',
        'Karnataka': 'Bengaluru',
        'Kerala': 'Thiruvananthapuram',
        'Madhya Pradesh': 'Bhopal',
        'Maharashtra': 'Mumbai',
        'Manipur': 'Imphal',
        'Meghalaya': 'Shillong',
        'Mizoram': 'Aizawl',
        'Nagaland': 'Kohima',
        'Odisha': 'Bhubaneswar',
        'Punjab': 'Chandigarh',
        'Rajasthan': 'Jaipur',
        'Sikkim': 'Gangtok',
        'Tamil Nadu': 'Chennai',
        'Telangana': 'Hyderabad',
        'Tripura': 'Agartala',
        'Uttar Pradesh': 'Lucknow',
        'Uttarakhand': 'Dehradun',
        'West Bengal': 'Kolkata',
        'Delhi': 'New Delhi',
        'Jammu and Kashmir': 'Srinagar',
        'Ladakh': 'Leh',
        'Puducherry': 'Puducherry'
      };

      const stateLocation = stateCapitals[userContext.state] || userContext.state;
      console.log(`Using state capital/major city for ${userContext.state}:`, stateLocation);
      return stateLocation;
    }

    console.log('Using default location: New Delhi');
    return 'New Delhi'; // Default fallback
  };

  const effectiveLocation = getEffectiveLocation();
  const effectiveIsDistrictView = isDistrictView || userContext.type === 'district';
  const effectiveIsStateView = isStateView || userContext.type === 'state';

  const cardStyle = {
    background: (isDistrictView || effectiveIsStateView) ?
      'linear-gradient(to right, #4facfe, #00f2fe)' :
      effectiveIsStateView ?
        'linear-gradient(to right, #667eea, #764ba2)' :
        'linear-gradient(to right, #4facfe, #00f2fe)',
    borderRadius: '20px',
    padding: '25px',
    color: 'white',
    marginBottom: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    boxShadow: (isDistrictView || effectiveIsStateView) ?
      '0 10px 30px rgba(255, 107, 53, 0.3)' :
      effectiveIsStateView ?
        '0 10px 30px rgba(102, 126, 234, 0.3)' :
        '0 10px 30px rgba(0, 0, 0, 0.2)',
    position: 'relative',
    overflow: 'hidden',
    border: (isDistrictView || effectiveIsStateView) ? '2px solid #00f2fe' : 'none',
  };

  const cityTitleStyle = {
    fontSize: '24px',
    fontWeight: '700',
    marginBottom: '20px',
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const rowStyle = {
    display: 'flex',
    justifyContent: 'space-around',
    width: '100%',
    marginTop: '10px',
    gap: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: '15px',
    padding: '15px',
    backdropFilter: 'blur(10px)',
    flexWrap: 'wrap',
  };

  const itemStyle = {
    textAlign: 'center',
    flex: '1 1 120px',
    minWidth: '100px',
    padding: '12px',
    borderRadius: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
  };

  const aqiCardStyle = {
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.85))',
    borderRadius: '16px',
    padding: '20px',
    marginTop: '20px',
    width: '100%',
    boxShadow: '0 4px 15px rgba(30, 60, 114, 0.2)',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease',
    color: '#1e3c72',
  };

  const aqiHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
  };

  const aqiNumberStyle = {
    fontSize: '48px',
    fontWeight: '800',
    lineHeight: '1',
    textShadow: '0 2px 8px rgba(30, 60, 114, 0.2)',
    color: '#1e3c72',
  };

  const aqiLabelStyle = {
    fontSize: '13px',
    opacity: 0.8,
    marginTop: '6px',
    fontWeight: '600',
    letterSpacing: '0.5px',
    color: '#2a5298',
  };

  const aqiCategoryBadgeStyle = {
    padding: '10px 20px',
    borderRadius: '12px',
    fontWeight: '700',
    fontSize: '15px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };

  const pollutantsRowStyle = {
    display: 'flex',
    gap: '15px',
    marginTop: '15px',
  };

  const pollutantItemStyle = {
    flex: 1,
    textAlign: 'center',
    padding: '15px 10px',
    backgroundColor: 'rgba(30, 60, 114, 0.08)',
    borderRadius: '12px',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    border: '1px solid rgba(30, 60, 114, 0.1)',
  };

  const aqiTitleStyle = {
    fontSize: '14px',
    fontWeight: '700',
    marginBottom: '12px',
    opacity: 0.9,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#1e3c72',
  };

  useEffect(() => {
    const locationQuery = effectiveLocation;

    setLoading(true);
    setError(null);

    fetch(`${process.env.REACT_APP_API_URL || "http://localhost:5001"}/api/india/weather/dynamic?q=${encodeURIComponent(locationQuery)}`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
      .then(json => {
        if (json.error) throw new Error(json.error);

        let displayCityName = json.city || locationQuery;
        if (userContext.type === 'state' && userContext.state) {
          displayCityName = userContext.state;
        } else if (userContext.type === 'district' && userContext.district) {
          displayCityName = userContext.district;
        }

        setCity(displayCityName);
        setWeather(json.current || null);
        setAqiData(json.aqiData || null);
        setError(null);
      })
      .catch(err => {
        setError(err.message);
        let fallbackCityName = locationQuery;
        if (userContext.type === 'state' && userContext.state) fallbackCityName = userContext.state;
        else if (userContext.type === 'district' && userContext.district) fallbackCityName = userContext.district;
        setCity(fallbackCityName);
        setWeather(null);
        setAqiData(null);
      })
      .finally(() => setLoading(false));
  }, [effectiveLocation, userContext.type, userContext.state, userContext.district, location]);

  if (loading) {
    // Determine what name to show while loading
    let loadingLocationName = effectiveLocation;
    if (userContext.type === 'state' && userContext.state) {
      loadingLocationName = userContext.state;
    } else if (userContext.type === 'district' && userContext.district) {
      loadingLocationName = userContext.district;
    }

    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '20px',
            height: '20px',
            border: '2px solid rgba(255,255,255,0.3)',
            borderTop: '2px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p>Loading weather data for {loadingLocationName}...</p>
        </div>
      </div>
    );
  }

  if (error) {
    // Determine what name to show in error state
    let errorLocationName = effectiveLocation;
    if (userContext.type === 'state' && userContext.state) {
      errorLocationName = userContext.state;
    } else if (userContext.type === 'district' && userContext.district) {
      errorLocationName = userContext.district;
    }

    return (
      <div style={{ ...cardStyle, background: 'linear-gradient(to right, #ff6b6b, #ee5a52)' }}>
        <h3 style={cityTitleStyle}>
          ⚠️ Weather Data Unavailable
        </h3>
        <p>Unable to fetch weather data for {errorLocationName}</p>
        <p style={{ fontSize: '14px', opacity: 0.8 }}>Error: {error}</p>
      </div>
    );
  }

  if (!weather) {
    return (
      <div style={{ ...cardStyle, background: 'linear-gradient(to right, #ffa726, #ff9800)' }}>
        <h3 style={cityTitleStyle}>
          📍 {city}
        </h3>
        <p>No weather data available for this location</p>
      </div>
    );
  }

  return (
    <div style={cardStyle}>

      <h3 style={cityTitleStyle}>
        {effectiveIsDistrictView ? '🏛️' : effectiveIsStateView ? '🏛️' : '📍'} {city}
        {effectiveIsDistrictView && (
          <span style={{
            fontSize: '14px',
            opacity: 0.8,
            marginLeft: '10px',
            backgroundColor: 'rgba(255,255,255,0.2)',
            padding: '4px 8px',
            borderRadius: '12px'
          }}>
            District Weather
          </span>
        )}
        {effectiveIsStateView && !effectiveIsDistrictView && (
          <span style={{
            fontSize: '14px',
            opacity: 0.8,
            marginLeft: '10px',
            backgroundColor: 'rgba(255,255,255,0.2)',
            padding: '4px 8px',
            borderRadius: '12px'
          }}>
            State Weather
          </span>
        )}
      </h3>
      <div style={rowStyle}>
        <div
          style={itemStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
          }}
        >
          <h2 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700' }}>
            {weather.temp_c != null ? `${weather.temp_c}°C` : '--'}
          </h2>
          <p style={{ margin: 0, fontSize: '13px', opacity: 0.9 }}>🌡️ Temperature</p>
        </div>
        <div
          style={itemStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
          }}
        >
          <h2 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700' }}>
            {weather.humidity != null ? `${weather.humidity}%` : '--'}
          </h2>
          <p style={{ margin: 0, fontSize: '13px', opacity: 0.9 }}>💧 Humidity</p>
        </div>
        <div
          style={itemStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
          }}
        >
          <h2 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700' }}>
            {weather.precip_mm != null ? `${weather.precip_mm}` : '--'}
          </h2>
          <p style={{ margin: 0, fontSize: '13px', opacity: 0.9 }}>🌧️ Precip (mm)</p>
        </div>
        <div
          style={itemStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
          }}
        >
          <h2 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700' }}>
            {weather.wind_kph != null ? `${weather.wind_kph}` : '--'}
          </h2>
          <p style={{ margin: 0, fontSize: '13px', opacity: 0.9 }}>💨 Wind (kph)</p>
        </div>

        {/* AQI inline with other metrics */}
        {aqiData && (
          <div
            style={{
              ...itemStyle,
              borderLeft: `4px solid ${aqiData.color}`,
              position: 'relative',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
            }}
          >
            <h2 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700' }}>
              {aqiData.aqi}
            </h2>
            <p style={{ margin: 0, fontSize: '13px', opacity: 0.9 }}>💨 AQI (US)</p>
            <div style={{
              marginTop: '8px',
              padding: '4px 8px',
              borderRadius: '6px',
              backgroundColor: aqiData.color,
              color: 'white',
              fontSize: '10px',
              fontWeight: '700',
              textTransform: 'uppercase',
            }}>
              {aqiData.category}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeatherWidget;